const getApiUrl = () => {
  // Priority 1: Environment variable
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) {
    return envUrl.trim();
  }

  // Fallback : use http (backend runs on HTTP)
  const hostname = window.location.hostname.trim();
  const port = '3000';
  return `http://${hostname}:${port}/api`;
};

const API_URL = getApiUrl();

// Fonction utilitaire pour décoder un token JWT (côté client)
const decodeToken = (token) => {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (e) {
    return null;
  }
};

// Vérifier si un token est expiré
const isTokenExpired = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  return Date.now() >= decoded.exp * 1000;
};

// Helper function to get auth token
const getToken = () => {
  const token = sessionStorage.getItem('token');
  return token;
};

// Helper function pour déconnexion propre
const forceLogout = () => {
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('refreshToken');
  sessionStorage.removeItem('user');
  // Déclencher un événement pour que l'app sache qu'on a été déconnecté
  window.dispatchEvent(new Event('auth:logout'));
  // La redirection vers login sera gérée automatiquement par les routes protégées
};

// Helper function for API requests
const apiRequest = async (endpoint, options = {}) => {
  const token = getToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });

    const data = await response.json();

    // Gestion centralisée des erreurs 401 (token invalide/expiré)
    if (response.status === 401) {
      console.error('Token invalide ou expiré (401) pour:', endpoint);
      
      // Vérifier si c'est vraiment une erreur d'auth ou une autre erreur 401
      if (data.error && (data.error.includes('token') || data.error.includes('Token') || data.error.includes('Unauthorized') || data.error.includes('Invalid'))) {
        console.log('Déconnexion forcée: token invalide');
        forceLogout();
        throw new Error('Session expirée');
      }
      // Autres erreurs 401 (ex: ressource non autorisée)
      throw new Error(data.error || 'Non autorisé');
    }

    if (!response.ok) {
      throw new Error(data.error || 'Something went wrong');
    }

    return data;
  } catch (err) {
    // Network error (backend unreachable)
    if (err.name === 'TypeError' && err.message.includes('fetch')) {
      console.error('Erreur réseau: backend injoignable');
      throw new Error('Network error: Backend unreachable');
    }
    throw err;
  }
};

// Auth API
export const authAPI = {
  register: (userData) => apiRequest('/auth/register', {
    method: 'POST',
    body: JSON.stringify(userData),
  }),

  login: (credentials) => apiRequest('/auth/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  }),

  verifyOTP: (email, otp) => apiRequest('/auth/verify-otp', {
    method: 'POST',
    body: JSON.stringify({ email, otp }),
  }),

  sendOTP: () => apiRequest('/auth/send-otp', {
    method: 'POST',
  }),

  resendOTP: (email) => apiRequest('/auth/resend-otp', {
    method: 'POST',
    body: JSON.stringify({ email }),
  }),

  enable2FA: (token) => apiRequest('/auth/enable-2fa', {
    method: 'POST',
    body: JSON.stringify({ token }),
  }),

  disable2FA: (password) => apiRequest('/auth/disable-2fa', {
    method: 'POST',
    body: JSON.stringify({ password }),
  }),

  getMe: () => apiRequest('/auth/me'),

  updateProfile: (updates) => apiRequest('/auth/profile', {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),

  changePassword: (currentPassword, newPassword) => apiRequest('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  }),

  forgotPassword: (email) => apiRequest('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  }),

  resetPassword: (token, newPassword) => apiRequest('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, newPassword }),
  }),
};

// Admin API
export const adminAPI = {
  getUsers: () => apiRequest('/admin/users'),
  
  createUser: (userData) => apiRequest('/admin/users', {
    method: 'POST',
    body: JSON.stringify(userData),
  }),
  
  getUser: (userId) => apiRequest(`/admin/users/${userId}`),
  
  updateUserRole: (userId, role) => apiRequest(`/admin/users/${userId}/role`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
  }),

  toggleUserStatus: (userId) => apiRequest(`/admin/users/${userId}/status`, {
    method: 'PUT',
  }),

  resetUserPassword: (userId, newPassword) => apiRequest(`/admin/users/${userId}/password`, {
    method: 'PUT',
    body: JSON.stringify({ newPassword }),
  }),

  deleteUser: (userId) => apiRequest(`/admin/users/${userId}`, {
    method: 'DELETE',
  }),

  getMeetings: () => apiRequest('/admin/meetings'),
  
  getMeeting: (meetingId) => apiRequest(`/admin/meetings/${meetingId}`),
  
  updateMeeting: (meetingId, updates) => apiRequest(`/admin/meetings/${meetingId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),

  deleteMeeting: (meetingId) => apiRequest(`/admin/meetings/${meetingId}`, {
    method: 'DELETE',
  }),

  getMeetingStats: () => apiRequest('/admin/meetings/stats'),

  getSettings: () => apiRequest('/admin/settings'),

  updateSettings: (updates) => apiRequest('/admin/settings', {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),

  resetSettings: () => apiRequest('/admin/settings/reset', {
    method: 'POST',
  }),

  getStats: () => apiRequest('/admin/stats'),

  getRooms: () => apiRequest('/admin/rooms'),
};

// Meetings API
export const meetingsAPI = {
  schedule: (meetingData) => apiRequest('/meetings', {
    method: 'POST',
    body: JSON.stringify(meetingData),
  }),

  getAll: (type = 'all') => apiRequest(`/meetings?type=${type}`),

  getUpcoming: () => apiRequest('/meetings/upcoming'),

  getMeeting: (meetingId) => apiRequest(`/meetings/${meetingId}`),

  updateMeeting: (meetingId, updates) => apiRequest(`/meetings/${meetingId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),

  deleteMeeting: (meetingId) => apiRequest(`/meetings/${meetingId}`, {
    method: 'DELETE',
  }),

  startMeeting: (meetingId, roomId) => apiRequest(`/meetings/${meetingId}/start`, {
    method: 'POST',
    body: JSON.stringify({ roomId }),
  }),

  endMeeting: (meetingId) => apiRequest(`/meetings/${meetingId}/end`, {
    method: 'POST',
  }),

  getRoles: () => apiRequest('/meetings/roles'),

  fixMeetingsStatus: () => apiRequest('/meetings/fix-status', {
    method: 'POST',
  }),

  getUserMeetings: (userId) => apiRequest(`/meetings/user/${userId}`),

  createRoom: (roomData) => apiRequest('/meetings/create-room', {
    method: 'POST',
    body: JSON.stringify(roomData),
  }),

  getAllOwned: (userId) => apiRequest(`/meetings/user/${userId}`),

  updateRoom: (roomId, updates) => apiRequest(`/meetings/${roomId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),

  deleteRoom: (roomId) => apiRequest(`/meetings/${roomId}`, {
    method: 'DELETE',
  }),

  updateRoomSettings: (roomId, settings) => apiRequest(`/meetings/${roomId}/settings`, {
    method: 'PUT',
    body: JSON.stringify(settings),
  }),
};

// Messages API (pour ChatRoom)
export const messagesAPI = {
   getByRoom: (roomId) => apiRequest(`/rooms/${roomId}/messages`),

   send: (roomId, data) => apiRequest(`/rooms/${roomId}/messages`, {
     method: 'POST',
     body: JSON.stringify(data),
   }),

   update: (messageId, contenu) => apiRequest(`/rooms/messages/${messageId}`, {
     method: 'PUT',
     body: JSON.stringify({ contenu }),
   }),

   delete: (messageId) => apiRequest(`/rooms/messages/${messageId}`, {
     method: 'DELETE',
   }),

   getById: (messageId) => apiRequest(`/rooms/messages/${messageId}`),
};

export const roomsAPI = {
  create: (roomData) => apiRequest('/rooms', {
    method: 'POST',
    body: JSON.stringify(roomData),
  }),

  getAll: (type = 'all') => apiRequest(`/rooms?type=${type}`),

  getRoom: (roomId) => apiRequest(`/rooms/${roomId}`),

  updateRoom: (roomId, updates) => apiRequest(`/rooms/${roomId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),

  deleteRoom: (roomId) => apiRequest(`/rooms/${roomId}`, {
    method: 'DELETE',
  }),

  toggleRoomStatus: (roomId) => apiRequest(`/rooms/${roomId}/status`, {
    method: 'PUT',
  }),

  addParticipant: (roomId, email, role = 'member') => apiRequest(`/rooms/${roomId}/participants`, {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  }),

  removeParticipant: (roomId, userId) => apiRequest(`/rooms/${roomId}/participants/${userId}`, {
    method: 'DELETE',
  }),

  joinRoom: (roomId) => apiRequest(`/rooms/${roomId}/join`, {
    method: 'POST',
  }),

  leaveRoom: (roomId) => apiRequest(`/rooms/${roomId}/leave`, {
    method: 'POST',
  }),

  getParticipants: (roomId) => apiRequest(`/rooms/${roomId}/participants`),

  toggleNotifications: (roomId, enabled) => apiRequest(`/rooms/${roomId}/notifications`, {
    method: 'PUT',
    body: JSON.stringify({ enabled }),
  }),

  startMeeting: (roomId, title, description) => apiRequest(`/rooms/${roomId}/start-meeting`, {
    method: 'POST',
    body: JSON.stringify({ title, description }),
  }),

  endMeeting: (roomId) => apiRequest(`/rooms/${roomId}/end-meeting`, {
    method: 'POST',
  }),

  getMeetingHistory: (roomId) => apiRequest(`/rooms/${roomId}/meetings`),

  createInvitation: (roomId, options) => apiRequest(`/rooms/${roomId}/invitations`, {
    method: 'POST',
    body: JSON.stringify(options),
  }),

  getInvitations: (roomId) => apiRequest(`/rooms/${roomId}/invitations`),

  deleteInvitation: (roomId, invitationId) => apiRequest(`/rooms/${roomId}/invitations/${invitationId}`, {
    method: 'DELETE',
  }),

  getInvitationInfo: (token) => apiRequest(`/rooms/join/${token}`),

  joinViaInvitation: (token) => apiRequest(`/rooms/join/${token}`, {
    method: 'POST',
  }),

  getMessages: (roomId) => apiRequest(`/rooms/${roomId}/messages`),

  sendMessage: (roomId, message) => apiRequest(`/rooms/${roomId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ contenu: message }),
  }),

  sendInvite: (roomId, email) => apiRequest(`/rooms/${roomId}/invite`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  }),
};

// Recordings API
export const recordingsAPI = {
  create: (recordingData) => apiRequest('/recordings', {
    method: 'POST',
    body: JSON.stringify(recordingData),
  }),

uploadRecording: async (formData) => {
  const token = sessionStorage.getItem('token');
  const response = await fetch(`${API_URL}/recordings/upload`, {
    method: 'POST',
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
    body: formData,
  });
  const data = await response.json();
  if (!response.ok) {
    if (response.status === 401) {
      forceLogout();
    }
    const details = data.details ? JSON.stringify(data.details) : '';
    throw new Error(`${data.error || 'Failed to upload recording'}${details ? ' | ' + details : ''}`);
  }
  return data;
},

  getUserRecordings: (userId, limit = 20) => apiRequest(`/recordings/user/${userId}?limit=${limit}`),

  getRoomRecordings: (roomId) => apiRequest(`/recordings/room/${roomId}`),

  getMeetingRecordings: (meetingId) => apiRequest(`/recordings/meeting/${meetingId}`),

  deleteRecording: (id) => apiRequest(`/recordings/${id}`, {
    method: 'DELETE',
  }),

  shareRecording: (id, options = {}) => apiRequest(`/recordings/${id}/share`, {
    method: 'POST',
    body: JSON.stringify(options),
  }),
};

// Fichiers (Files) API
export const fichiersAPI = {
  getByMeeting: (id_meeting) => apiRequest(`/fichiers/meeting/${id_meeting}`),

  uploadFile: async (formData) => {
    const token = sessionStorage.getItem('token');
    const response = await fetch(`${API_URL}/fichiers/upload`, {
      method: 'POST',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
      body: formData,
    });
    const data = await response.json();
    if (!response.ok) {
      if (response.status === 401) {
        forceLogout();
      }
      throw new Error(data.error || 'Failed to upload file');
    }
    return data;
  },

  getUserFiles: (userId) => apiRequest(`/fichiers/user/${userId}`),
};

export default API_URL;
